import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Clock, MessageSquare, Edit2, X, Check, Eye, History } from "lucide-react";
import { SequenceExportCard } from "./SequenceExportCard";
import { TargetDetailDialog } from "./TargetDetailDialog";
import { FailureAnalysis } from "./FailureAnalysis";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { format } from "date-fns";
import { formatCapacityError, isCapacityError } from "@/hooks/useAccountCapacity";


interface CampaignDetailsDialogProps {
  campaignId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
}

export const CampaignDetailsDialog = ({ campaignId, open, onOpenChange, defaultTab = "overview" }: CampaignDetailsDialogProps) => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isActuallySuperAdmin } = useWorkspace();
  const [isEditingLimits, setIsEditingLimits] = useState(false);
  const [editedLimits, setEditedLimits] = useState({ maxConnectionsDaily: 0, maxMessagesDaily: 0 });
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const { data: campaign } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select(`
          *,
          clients (
            id,
            email,
            status,
            max_connections_per_day,
            max_messages_per_day
          )
        `)
        .eq("id", campaignId)
        .single();
      
      if (error) throw error;
      
      // Use stored limits
      const maxConnectionsDaily = data.max_connections_daily || 20;
      const maxMessagesDaily = data.max_messages_daily || 20;
      
      setEditedLimits({ maxConnectionsDaily, maxMessagesDaily });
      
      return data;
    },
    enabled: open,
  });

  const { data: targets } = useQuery({
    queryKey: ["campaign-targets", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("targets")
        .select("*")
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const { data: sequences } = useQuery({
    queryKey: ["campaign-sequences", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequences")
        .select("*")
        .eq("campaign_id", campaignId)
        .order("step_number");
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Analytics data queries
  const { data: analyticsData } = useQuery({
    queryKey: ["campaign-analytics", campaignId],
    queryFn: async () => {
      if (!targets) return null;

      // Group targets by date for timeline
      const timelineData = targets.reduce((acc: any, target) => {
        const date = new Date(target.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = { date, connections: 0, messages: 0, replies: 0 };
        }
        if (target.connection_sent_at) acc[date].connections++;
        if (target.first_message_sent_at) acc[date].messages++;
        if (target.reply_received_at) acc[date].replies++;
        return acc;
      }, {});

      // Calculate response times
      const responseTimes = targets
        .filter(t => t.first_message_sent_at && t.reply_received_at)
        .map(t => {
          const sent = new Date(t.first_message_sent_at!).getTime();
          const replied = new Date(t.reply_received_at!).getTime();
          return Math.round((replied - sent) / (1000 * 60 * 60)); // hours
        });

      const avgResponseTime = responseTimes.length > 0
        ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
        : 0;

      return {
        timeline: Object.values(timelineData),
        avgResponseTime,
        responseTimes,
      };
    },
    enabled: open && !!targets,
  });

  // Import history for super admins
  const { data: importHistory } = useQuery({
    queryKey: ["campaign-import-history", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_import_history')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: open && isSuperAdmin,
  });

  // Mutation to update campaign limits
  const updateLimitsMutation = useMutation({
    mutationFn: async () => {
      if (!campaign) return;
      
      const { error } = await supabase
        .from("campaigns")
        .update({
          max_connections_daily: editedLimits.maxConnectionsDaily,
          max_messages_daily: editedLimits.maxMessagesDaily,
        })
        .eq("id", campaignId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign limits updated successfully");
      setIsEditingLimits(false);
    },
    onError: (error: any) => {
      toast.error(
        isCapacityError(error)
          ? formatCapacityError(error)
          : "Failed to update limits: " + error.message
      );

    },
  });

  if (!campaign) return null;

  const client = campaign.clients as any;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{campaign.name}</DialogTitle>
            <StatusBadge status={campaign.status as any} />
          </div>
          <p className="text-sm text-muted-foreground">
            Client: {client?.email} • Created: {new Date(campaign.created_at).toLocaleDateString()}
          </p>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="mt-4">
          <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-7' : 'grid-cols-6'}`}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="targets">Targets ({targets?.length || 0})</TabsTrigger>
            <TabsTrigger value="failures">
              Failures ({targets?.filter(t => t.status === 'failed').length || 0})
            </TabsTrigger>
            <TabsTrigger value="sequence">Sequence ({sequences?.length || 0})</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="imports">
                <History className="w-3 h-3 mr-1" />
                Imports
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Total Targets</p>
                <p className="text-2xl font-bold">{targets?.length || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Connected</p>
                <p className="text-2xl font-bold text-linkedin">
                  {targets?.filter(t => t.status === "connected").length || 0}
                </p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Replied</p>
                <p className="text-2xl font-bold text-success">
                  {targets?.filter(t => t.status === "replied").length || 0}
                </p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Failed</p>
                <p className="text-2xl font-bold text-destructive">
                  {targets?.filter(t => t.status === "failed").length || 0}
                </p>
              </Card>
            </div>

            <Card className="p-4">
              <h3 className="font-semibold mb-3">Status Distribution</h3>
              <div className="space-y-2">
                {["pending", "visited", "connected", "messaged", "replied", "failed"].map(status => {
                  const count = targets?.filter(t => t.status === status).length || 0;
                  const percentage = targets?.length ? (count / targets.length) * 100 : 0;
                  
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <StatusBadge status={status as any} className="w-24" />
                      <div className="flex-1">
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium w-12 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="mt-4 space-y-4">
            {/* Response Timeline Chart */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Response Timeline
              </h3>
              {analyticsData?.timeline && analyticsData.timeline.length > 0 ? (
                <ChartContainer
                  config={{
                    connections: { label: "Connections", color: "hsl(var(--chart-1))" },
                    messages: { label: "Messages", color: "hsl(var(--chart-2))" },
                    replies: { label: "Replies", color: "hsl(var(--chart-3))" },
                  }}
                  className="h-[300px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData.timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Line type="monotone" dataKey="connections" stroke="hsl(var(--chart-1))" strokeWidth={2} />
                      <Line type="monotone" dataKey="messages" stroke="hsl(var(--chart-2))" strokeWidth={2} />
                      <Line type="monotone" dataKey="replies" stroke="hsl(var(--chart-3))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">No timeline data yet</p>
              )}
            </Card>

            {/* Average Response Time */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Average Response Time</p>
                  <p className="text-3xl font-bold flex items-center gap-2">
                    <Clock className="w-6 h-6 text-primary" />
                    {analyticsData?.avgResponseTime || 0}h
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1">Total Conversations</p>
                  <p className="text-3xl font-bold text-success">
                    {targets?.filter(t => t.reply_count && t.reply_count > 0).length || 0}
                  </p>
                </div>
              </div>
            </Card>

            {/* Target Performance Table */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Top Performing Targets
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Connection Date</TableHead>
                      <TableHead>Reply Date</TableHead>
                      <TableHead>Response Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {targets
                      ?.filter(t => t.status === "replied")
                      .slice(0, 10)
                      .map((target) => {
                        const responseTime = target.first_message_sent_at && target.reply_received_at
                          ? Math.round((new Date(target.reply_received_at).getTime() - new Date(target.first_message_sent_at).getTime()) / (1000 * 60 * 60))
                          : null;

                        return (
                          <TableRow key={target.id}>
                            <TableCell className="font-medium">
                              {target.first_name} {target.last_name}
                            </TableCell>
                            <TableCell>{target.company || "-"}</TableCell>
                            <TableCell>
                              <StatusBadge status={target.status as any} />
                            </TableCell>
                            <TableCell className="text-xs">
                              {target.connection_accepted_at
                                ? new Date(target.connection_accepted_at).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell className="text-xs">
                              {target.reply_received_at
                                ? new Date(target.reply_received_at).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {responseTime ? (
                                <Badge variant="outline" className="text-xs">
                                  {responseTime}h
                                </Badge>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                {(!targets || targets.filter(t => t.status === "replied").length === 0) && (
                  <p className="text-center py-8 text-muted-foreground">No replies yet</p>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="targets" className="mt-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>LinkedIn URL</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {targets?.map((target) => (
                    <TableRow key={target.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedTargetId(target.id)}>
                      <TableCell>
                        {target.first_name} {target.last_name}
                      </TableCell>
                      <TableCell>{target.company || "-"}</TableCell>
                      <TableCell>{target.title || "-"}</TableCell>
                      <TableCell>
                        <StatusBadge status={target.status as any} />
                      </TableCell>
                      <TableCell>
                        <a 
                          href={target.profile_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Profile
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTargetId(target.id);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="failures" className="mt-4">
            <FailureAnalysis campaignId={campaignId} />
          </TabsContent>

          <TabsContent value="sequence" className="mt-4 space-y-3">
            {isActuallySuperAdmin && sequences && sequences.length > 0 && (
              <div className="flex justify-end">
                <SequenceExportCard
                  sequences={sequences}
                  campaignName={campaign.name}
                  clientEmail={client?.email}
                />
              </div>
            )}
            {sequences?.map((seq) => (
              <Card key={seq.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-sm">{seq.step_number}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge>{seq.action_type}</Badge>
                      {seq.delay_hours > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Wait {seq.delay_hours} hour{seq.delay_hours > 1 ? "s" : ""}
                        </span>
                      )}
                      {(seq as any).use_inmail && (
                        <Badge variant="secondary" className="text-xs">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          InMail
                        </Badge>
                      )}
                    </div>
                    {seq.message_template && (
                      <div className="bg-muted/30 rounded p-3 text-sm">
                        {seq.message_template}
                      </div>
                    )}
                    {(seq as any).attachment_urls && Array.isArray((seq as any).attachment_urls) && (seq as any).attachment_urls.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {((seq as any).attachment_urls as string[]).map((url: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            📎 {url.split('/').pop() || 'attachment'}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold mb-3">Campaign Schedule</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-muted/30 rounded p-3">
                    <p className="text-xs text-muted-foreground">Timezone</p>
                    <p className="text-base font-medium">{campaign.timezone || 'America/New_York'}</p>
                  </div>
                <div className="bg-muted/30 rounded p-3">
                  <p className="text-xs text-muted-foreground">Working Hours</p>
                  <p className="text-base font-medium">
                    {campaign.working_hours_start === 0 && campaign.working_hours_end === 23 
                      ? "24/7" 
                      : `${campaign.working_hours_start ?? 9}:00 - ${campaign.working_hours_end ?? 18}:00`}
                  </p>
                </div>
                </div>
                <div className="bg-muted/30 rounded p-3">
                  <p className="text-xs text-muted-foreground mb-2">Active Days</p>
                  <div className="flex flex-wrap gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                      const dayNum = idx + 1;
                      const isActive = campaign.working_days?.includes(dayNum);
                      return (
                        <Badge key={day} variant={isActive ? "default" : "outline"}>
                          {day}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Campaign Limits</h3>
                  {(campaign.status === "active" || campaign.status === "paused" || campaign.status === "draft") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (isEditingLimits) {
                          setIsEditingLimits(false);
                          // Reset to original values
                          const maxConn = campaign.max_connections_daily || 20;
                          const maxMsg = campaign.max_messages_daily || 20;
                          setEditedLimits({
                            maxConnectionsDaily: maxConn,
                            maxMessagesDaily: maxMsg
                          });
                        } else {
                          setIsEditingLimits(true);
                        }
                      }}
                    >
                      {isEditingLimits ? (
                        <>
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit Limits
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {isEditingLimits ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Note:</strong> Changes will apply to future actions. Active jobs use the limits they were scheduled with.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Connections/Day</label>
                        <Input
                          type="number"
                          min="1"
                          max={client?.max_connections_per_day || 20}
                          value={editedLimits.maxConnectionsDaily}
                          onChange={(e) => setEditedLimits({ ...editedLimits, maxConnectionsDaily: parseInt(e.target.value) || 0 })}
                        />
                        <p className="text-xs text-muted-foreground">
                          Account max: {client?.max_connections_per_day || 20}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Messages/Day</label>
                        <Input
                          type="number"
                          min="1"
                          max={client?.max_messages_per_day || 20}
                          value={editedLimits.maxMessagesDaily}
                          onChange={(e) => setEditedLimits({ ...editedLimits, maxMessagesDaily: parseInt(e.target.value) || 0 })}
                        />
                        <p className="text-xs text-muted-foreground">
                          Account max: {client?.max_messages_per_day || 20}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => updateLimitsMutation.mutate()}
                      disabled={updateLimitsMutation.isPending}
                      className="w-full"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {updateLimitsMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/30 rounded p-3">
                      <p className="text-xs text-muted-foreground">Daily Connections</p>
                      <p className="text-xl font-bold">{editedLimits.maxConnectionsDaily}</p>
                    </div>
                    <div className="bg-muted/30 rounded p-3">
                      <p className="text-xs text-muted-foreground">Daily Messages</p>
                      <p className="text-xl font-bold">{editedLimits.maxMessagesDaily}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="imports" className="mt-4">
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-primary" />
                  Import History
                </h3>
                {importHistory && importHistory.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Source URL</TableHead>
                          <TableHead>Pages</TableHead>
                          <TableHead>Targets</TableHead>
                          <TableHead>Imported By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importHistory.map((record: any) => (
                          <TableRow key={record.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {format(new Date(record.created_at), 'MMM d, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              <Badge variant={record.import_type === 'csv' ? 'secondary' : 'default'}>
                                {record.import_type === 'csv' ? 'CSV' : 'Sales Nav'}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              {record.search_url === 'csv_upload' ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <a
                                  href={record.search_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-xs truncate block"
                                  title={record.search_url}
                                >
                                  {record.search_url.length > 50
                                    ? record.search_url.substring(0, 50) + '...'
                                    : record.search_url}
                                </a>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {record.import_type === 'csv' ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                `${record.start_page}–${record.end_page}`
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{record.target_count}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {record.imported_by
                                ? record.imported_by.substring(0, 8) + '...'
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No import history yet</p>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
      
      {/* Target Detail Dialog */}
      <TargetDetailDialog
        targetId={selectedTargetId}
        open={!!selectedTargetId}
        onOpenChange={(open) => !open && setSelectedTargetId(null)}
      />
    </Dialog>
  );
};
